<template>
  <ion-page>
    <div v-if="isStaging" class="odn-staging-bar"></div>
    <ion-tabs>
      <ion-router-outlet></ion-router-outlet>
      <ion-tab-bar slot="bottom" @click.capture="onTabBarClick">
        <ion-tab-button tab="stores" :disabled="!tabs" href="/tabs/stores">
          <ion-icon :icon="icons.storefrontOutline"></ion-icon>
        </ion-tab-button>

        <ion-tab-button
          v-if="hasFeatProjects"
          tab="projects"
          :disabled="!tabs"
          href="/tabs/projects"
        >
          <ion-icon :icon="icons.fileTrayOutline"></ion-icon>
        </ion-tab-button>

        <ion-tab-button
          v-if="hasFeatAppointments"
          tab="appointments"
          :disabled="!tabs"
          href="/tabs/appointments/list"
        >
          <ion-icon :icon="icons.calendarOutline"></ion-icon>
        </ion-tab-button>

        <ion-tab-button
          v-if="hasFeatProductScan"
          tab="products"
          :disabled="!tabs"
          href="/tabs/products/scan"
        >
          <ion-icon :icon="icons.barcodeOutline"></ion-icon>
        </ion-tab-button>

        <ion-tab-button tab="procedures" :disabled="!tabs" href="/tabs/help">
          <ion-icon :icon="icons.helpBuoyOutline"></ion-icon>
        </ion-tab-button>

        <ion-tab-button tab="profile" :disabled="!tabs" href="/tabs/profile">
          <ion-icon :icon="icons.personOutline"></ion-icon>
          <ion-badge v-if="notificationCount > 0" color="danger">
            {{ notificationCount }}
          </ion-badge>
        </ion-tab-button>
      </ion-tab-bar>
    </ion-tabs>
  </ion-page>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import {
  IonRouterOutlet,
  IonIcon,
  IonPage,
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonBadge,
  useIonRouter,
} from '@ionic/vue';
import {
  storefrontOutline,
  fileTrayOutline,
  calendarOutline,
  helpBuoyOutline,
  personOutline,
  barcodeOutline,
} from 'ionicons/icons';

import { isVueAppFlagEnabled } from '@s/environment';

export default {
  name: 'Tabs',
  components: {
    IonRouterOutlet,
    IonIcon,
    IonPage,
    IonTabBar,
    IonTabButton,
    IonTabs,
    IonBadge,
  },
  setup() {
    return { ionRouter: useIonRouter() };
  },
  data() {
    return {
      refreshInterval: 60000,
      refreshHandler: null,
      visitedPaths: [],
      icons: {
        storefrontOutline,
        fileTrayOutline,
        calendarOutline,
        helpBuoyOutline,
        personOutline,
        barcodeOutline,
      },
    };
  },
  computed: {
    ...mapState('auth', ['loggedIn', 'authData']),
    ...mapState('notifications', ['notificationCount']),
    ...mapState('prefs', ['tabs']),
    hasFeatProjects() {
      return (
        isVueAppFlagEnabled('VUE_APP_FEAT_PROJECTS') &&
        this.$isAllowedModuleAction('PROJECTS', 'READ')
      );
    },
    hasFeatProductScan() {
      return isVueAppFlagEnabled('VUE_APP_FEAT_PRODUCT_SCAN');
    },
    hasFeatAppointments() {
      return this.$isAllowedModuleAction('APPOINTMENTS', 'READ');
    },
    isStaging() {
      return isVueAppFlagEnabled('VUE_APP_IS_STAGING');
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(route) {
        if (!this.visitedPaths.includes(route.path)) {
          this.visitedPaths.push(route.path);
        }
      },
    },
  },
  ionViewDidEnter() {
    this.fetchNotifications();

    this.refreshHandler = setInterval(
      this.fetchNotifications,
      this.refreshInterval
    );
  },
  ionViewDidLeave() {
    clearInterval(this.refreshHandler);
  },
  methods: {
    ...mapActions('notifications', ['fetchNotifications']),
    /**
     * Cliquer sur l'onglet déjà actif déclenche `resetTab` d'Ionic, qui remonte
     * l'historique jusqu'à la première entrée enregistrée pour cet onglet. Après
     * un refresh (ou un deep link), cette entrée est la page courante elle-même :
     * le clic ne fait donc rien. Dans ce cas seulement, on force la navigation
     * vers la racine de l'onglet.
     */
    onTabBarClick(event) {
      // `href` n'est pas reflété en attribut par le custom element : on lit la
      // propriété du composant Ionic.
      const button = event.target.closest('ion-tab-button');
      const href = button && button.href;

      if (!href || button.disabled) return;

      const path = this.$route.path;
      const isInsideTab = path === href || path.startsWith(`${href}/`);

      if (!isInsideTab || path === href || this.visitedPaths.includes(href)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.ionRouter.navigate(href, 'back', 'replace');
    },
  },
};
</script>
